import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useBilling } from '../../context/BillingContext';
import { updateProfile, deleteAccount } from '../../api/authApi';
import { Eyebrow } from '../../design-system';
import ActiveSessions from './ActiveSessions';
import SignInHistory from './SignInHistory';
import styles from './ProfilePage.module.css';

export const ProfilePage: React.FC = () => {
  const { user, token, updateUser } = useAuth();
  const { subscription } = useBilling();
  const navigate = useNavigate();
  // An Enterprise account's jurisdictions live on its enterprise profile, and the chat reads them
  // from there first. The generic wizard saves somewhere the chat ignores for Enterprise.
  const editProfilePath = subscription?.plan_tier === 'enterprise' ? '/enterprise/profile/edit' : '/onboarding';
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setFullName(user?.full_name ?? '');
  }, [user?.full_name]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmed = fullName.trim();
    if (!trimmed) {
      setError('Name cannot be empty.');
      return;
    }

    if (!token) {
      setError('You need to log in again before editing your profile.');
      return;
    }

    setSaving(true);
    try {
      const updated = await updateProfile(token, { full_name: trimmed });
      updateUser(updated);
      setSuccess('Profile updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!token) {
      setDeleteError('You need to log in again before deleting your account.');
      return;
    }

    setDeleteError(null);
    setSuccess(null);
    setDeleteLoading(true);
    try {
      const result = await deleteAccount(token);
      setConfirmDelete(false);
      setSuccess(result.detail || 'A confirmation email has been sent to your inbox.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request account deletion.';
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const parseJsonField = (field: string | null | undefined): string[] => {
    if (!field) return [];
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.mainCard}>
        {/* Header with gradient background */}
        <div className={styles.headerSection}>
          <div className={styles.headerContent}>
            <div className={styles.avatarCircle}>
              {user?.full_name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <Eyebrow tone="default" dot>PROFILE</Eyebrow>
              <h1 className={styles.userName}>{user?.full_name || 'User'}</h1>
              <p className={styles.userEmail}>{user?.email}</p>
            </div>
          </div>
          <button 
            onClick={() => navigate(editProfilePath)} 
            className={styles.editButton}
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit Profile
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        {/* Content Area */}
        <div className={styles.contentArea}>

          {/* Account Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Account Information</h2>
            </div>
            <form className={styles.form} onSubmit={handleSave}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Full Name</label>
                  <input
                    className={styles.formInput}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email Address</label>
                  <input
                    className={styles.formInput}
                    value={user?.email ?? ''}
                    disabled
                  />
                </div>
              </div>
              <button className={styles.saveButton} type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>

          <div className={styles.divider} />

          {/* Location Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Location</h2>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Country</span>
                <span className={styles.infoValue}>{user?.country || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>State/Province</span>
                <span className={styles.infoValue}>{user?.state || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Postal Code</span>
                <span className={styles.infoValue}>{user?.postal_code || 'Not set'}</span>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          {/* Organization Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Organization</h2>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Organization</span>
                <span className={styles.infoValue}>{user?.organization_name || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Industry</span>
                <span className={styles.infoValue}>{user?.industry || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Type</span>
                <span className={styles.infoValue}>{user?.organization_type || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Your Role</span>
                <span className={styles.infoValue}>{user?.job_role || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Team Size</span>
                <span className={styles.infoValue}>{user?.team_size || 'Not set'}</span>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          {/* Regulatory Preferences Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Regulatory Preferences</h2>
            </div>
            <div className={styles.preferencesContainer}>
              <div className={styles.preferenceGroup}>
                <span className={styles.preferenceLabel}>Primary Jurisdictions</span>
                <div className={styles.tagContainer}>
                  {parseJsonField(user?.primary_jurisdictions).length > 0 ? (
                    parseJsonField(user?.primary_jurisdictions).map((jurisdiction, idx) => (
                      <span key={idx} className={styles.tag}>{jurisdiction}</span>
                    ))
                  ) : (
                    <span className={styles.emptyText}>No jurisdictions selected</span>
                  )}
                </div>
              </div>
              <div className={styles.preferenceGroup}>
                <span className={styles.preferenceLabel}>Regulation Types</span>
                <div className={styles.tagContainer}>
                  {parseJsonField(user?.regulation_types).length > 0 ? (
                    parseJsonField(user?.regulation_types).map((type, idx) => (
                      <span key={idx} className={styles.tag}>{type}</span>
                    ))
                  ) : (
                    <span className={styles.emptyText}>No regulation types selected</span>
                  )}
                </div>
              </div>
              <div className={styles.preferenceGroup}>
                <span className={styles.preferenceLabel}>Use Cases</span>
                <div className={styles.tagContainer}>
                  {parseJsonField(user?.use_cases).length > 0 ? (
                    parseJsonField(user?.use_cases).map((useCase, idx) => (
                      <span key={idx} className={styles.tag}>{useCase}</span>
                    ))
                  ) : (
                    <span className={styles.emptyText}>No use cases selected</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.dangerSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M19 6l-.867 12.142A2 2 0 0 1 16.138 20H7.862a2 2 0 0 1-1.995-1.858L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Delete account</h2>
            </div>
            <div className={styles.dangerCard}>
              <p className={styles.dangerText}>Request permanent deletion of your Policyora account. A confirmation link will be sent to your email, and deletion will only happen after you approve it.</p>
              {deleteError && <div className={styles.error}>{deleteError}</div>}
              <div className={styles.dangerActions}>
                {confirmDelete ? (
                  <>
                    <button
                      type="button"
                      className={styles.dangerButton}
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? 'Sending…' : 'Send confirmation email'}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleteLoading}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.dangerGhostButton}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete account
                  </button>
                )}
              </div>
              {confirmDelete && <p className={styles.confirmMessage}>The account will only be deleted after you confirm the email link. This action cannot be undone once confirmed.</p>}
            </div>
            <ActiveSessions />
            <SignInHistory />
          </div>
        </div>
      </div>
    </div>
  );
};
